// src/cockpit4/workspaces/components/WeeklyOutlineCard.tsx
// ============================================
// 周度内容大纲 · 状态徽章 + 概要列表 + 详细大纲入口（纯展示组件）
//
// 定位：从 MarketingStudioWorkspace 拆分出的展示层——当前大纲状态标记
// （默认模板 / 已就绪）+ 每天一行主题概要 + 「详细大纲 · 讨论」按钮。
// 不含 loading/empty 分支：父组件三元已保证仅在 weeklyOutline 存在时渲染本卡。
//
// 视觉：与父文件一致的深色玻璃拟态（amber/emerald 徽章），字号 8-12px font-mono。
// ============================================

import { Sparkles, Check, FileText } from "lucide-react";

interface WeeklyOutlineCardProps {
  /** 是否处于"默认模板"状态（琥珀徽章）；否则显示"本周大纲已就绪"（翠绿徽章） */
  defaultApplied: boolean;
  /** 周次标签（如 "2026-W42"），用于概要点缀与文件路径展示 */
  weekLabel?: string;
  /** 每天一行概要：day（如 "周一"）+ topics（主题摘要） */
  summary: { day: string; topics: string }[];
  /** 点击「详细大纲 · 讨论」打开详情弹窗 */
  onOpenDetail: () => void;
}

export default function WeeklyOutlineCard({
  defaultApplied,
  weekLabel,
  summary,
  onOpenDetail,
}: WeeklyOutlineCardProps) {
  return (
    <>
      {/* v6.2.9 当前大纲状态：默认模板 / 已采纳 标记，清楚知道当前是哪份 */}
      <div className="flex items-center gap-2 mb-2">
        {defaultApplied && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-amber-300 bg-amber-500/10 border border-amber-500/20">
            <Sparkles size={8} />
            默认模板（可讨论调整或重新生成）
          </span>
        )}
        {!defaultApplied && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono text-emerald-300 bg-emerald-500/10 border border-emerald-500/20">
            <Check size={8} />
            本周大纲已就绪
          </span>
        )}
        <span className="text-[9px] font-mono text-gray-500">
          {weekLabel} · 10_Operations/Content/周度大纲-{weekLabel}.md
        </span>
      </div>
      {/* v6.1.22 概要：每天一行（主题摘要），字体 11px 可读；详情+Hermes 讨论在弹窗内 */}
      <div className="space-y-1 mb-2">
        {summary.length === 0 ? (
          <div className="text-[12px] font-mono text-gray-500">已生成大纲（点击「详细大纲」查看全文并讨论）</div>
        ) : (
          summary.map((d) => (
            <div key={d.day} className="flex items-start gap-2 text-[11px] font-mono">
              <span className="text-purple-300 flex-shrink-0 w-[76px]">{d.day}</span>
              <span className="text-gray-300 truncate">{d.topics}</span>
            </div>
          ))
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onOpenDetail}
          className="flex items-center gap-1 px-2.5 py-1 rounded text-[10px] font-mono text-white bg-purple-600/80 hover:bg-purple-600 transition-colors"
          title="打开详细大纲：基于当前（已采纳）大纲自动生成详细内容，右侧讨论详细内容"
        >
          <FileText size={10} />
          详细大纲 · 讨论
        </button>
      </div>
    </>
  );
}
